/**
 * Board snapshot: a single GET returns the full board (project, columns,
 * tasks, sprints, labels, members) for fast initial render.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/features/auth/authContext'
import { useTaskCreatedToast } from './taskCreatedToast'
import type { Project, ProjectColumn, Task, Sprint, Milestone, Label, WorkspaceMember } from '@friday/shared/realtime'

export interface BoardSnapshot {
  project: Project
  columns: ProjectColumn[]
  tasks: Task[]
  sprints: Sprint[]
  milestones: Milestone[]
  labels: Label[]
  members: WorkspaceMember[]
}

/**
 * Resolve a project by its key inside a workspace. The query key is shared
 * with the board header, the members screen and the settings screen, so all
 * of them read one cache entry instead of refetching the project list.
 */
export function useProjectByKey(wsId: string | undefined, pKey: string | undefined) {
  return useQuery({
    queryKey: ['project-by-key', wsId, pKey],
    queryFn: async () => {
      const list = await api<Project[]>(`/api/v1/workspaces/${wsId}/projects`)
      return list.find((p) => p.key === pKey) ?? null
    },
    enabled: !!wsId && !!pKey,
  })
}

export function useBoard(wsId: string | undefined, pId: string | undefined) {
  return useQuery({
    queryKey: ['board', wsId, pId],
    queryFn: async () => {
      const data = await api<BoardSnapshot>(`/api/v1/projects/${pId}/board`)
      return data
    },
    enabled: !!wsId && !!pId,
  })
}

/** Mutation: create a task. Returns the new task. */
export function useCreateTask(wsId: string | undefined, pId: string | undefined, columnId: string | undefined) {
  const qc = useQueryClient()
  const announce = useTaskCreatedToast(wsId)
  return useMutation({
    mutationFn: (input: { title: string; priority?: 'low' | 'medium' | 'high' | 'urgent'; sprintId?: string | null }) =>
      api<Task>(`/api/v1/projects/${pId}/tasks`, {
        method: 'POST',
        body: { ...input, columnId },
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: (task) => {
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      announce(task)
    },
  })
}

/** Mutation: move a task to a new column (and/or position). Optimistic. */
export function useMoveTask(wsId: string | undefined, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      taskId: string
      columnId: string
      prevTaskId?: string | null
      nextTaskId?: string | null
      version: number
    }) =>
      api<Task>(`/api/v1/tasks/${input.taskId}/move`, {
        method: 'POST',
        body: {
          columnId: input.columnId,
          prevTaskId: input.prevTaskId ?? null,
          nextTaskId: input.nextTaskId ?? null,
        },
      }),
    onMutate: async (input) => {
      const key = ['board', wsId, pId]
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<BoardSnapshot>(key)
      if (previous) {
        const tasks = previous.tasks.map((t) =>
          t.id === input.taskId
            ? { ...t, columnId: input.columnId, version: input.version + 1 }
            : t,
        )
        qc.setQueryData<BoardSnapshot>(key, { ...previous, tasks })
      }
      return { previous }
    },
    onError: (e, _input, ctx) => {
      if (e instanceof ApiError && e.status === 409) {
        // Version conflict — refetch.
        void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      }
      if (ctx?.previous) qc.setQueryData(['board', wsId, pId], ctx.previous)
    },
    onSettled: (_data, _error, variables) => {
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      void qc.invalidateQueries({ queryKey: ['task'] })
      void qc.invalidateQueries({ queryKey: ['subtasks'] })
      void qc.invalidateQueries({ queryKey: ['my-tasks'] })
      void qc.invalidateQueries({ queryKey: ['activity', 'task', variables.taskId] })
    },
  })
}

/** Mutation: update a task (e.g. title, priority, assignee). */
export function useUpdateTask(wsId: string | undefined, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['update-task', wsId, pId],
    mutationFn: (input: { taskId: string; version: number; patch: Record<string, unknown> }) =>
      api<Task>(`/api/v1/tasks/${input.taskId}?version=${input.version}`, {
        method: 'PATCH',
        body: input.patch,
      }),
    onMutate: async (input) => {
      const key = ['board', wsId, pId]
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<BoardSnapshot>(key)
      const taskKey = ['task', input.taskId]
      // A refetch still in flight from an earlier save must not overwrite this one.
      await qc.cancelQueries({ queryKey: taskKey })
      const previousTask = qc.getQueryData<Task>(taskKey)
      // FRIDAY-71: `labelNames` is shown at once as the task's labels.
      const { labelNames, ...rest } = input.patch as { labelNames?: string[] } & Record<string, unknown>
      const current = previousTask ?? previous?.tasks.find((t) => t.id === input.taskId)
      const labels = labelNames ? optimisticLabels(labelNames, previous?.labels ?? [], current?.labels ?? []) : undefined
      const apply = (t: Task) =>
        ({ ...t, ...rest, ...(labels ? { labels } : {}), version: input.version + 1 }) as Task
      if (previous) {
        const tasks = previous.tasks.map((t) => (t.id === input.taskId ? apply(t) : t))
        qc.setQueryData<BoardSnapshot>(key, { ...previous, tasks })
      }
      if (previousTask) qc.setQueryData<Task>(taskKey, apply(previousTask))
      return { previous, previousTask }
    },
    onError: (e, _input, ctx) => {
      if (e instanceof ApiError && e.status === 409) {
        void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      }
      if (ctx?.previous) qc.setQueryData(['board', wsId, pId], ctx.previous)
      if (ctx?.previousTask) qc.setQueryData(['task', _input.taskId], ctx.previousTask)
      if (ctx?.previousTask) qc.setQueryData(['task', _input.taskId], ctx.previousTask)
    },
    onSettled: (_data, _error, variables) => {
      // A later save of this board is still running: its own settle refetches,
      // and refetching now could bring back the older state (FRIDAY-71).
      if (qc.isMutating({ mutationKey: ['update-task', wsId, pId] }) > 1) return
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      void qc.invalidateQueries({ queryKey: ['task'] })
      void qc.invalidateQueries({ queryKey: ['subtasks'] })
      void qc.invalidateQueries({ queryKey: ['my-tasks'] })
      void qc.invalidateQueries({ queryKey: ['activity', 'task', variables.taskId] })
    },
  })
}

export function useTask(wsId: string | undefined, pId: string | undefined, taskId: string | undefined) {
  return useQuery({
    queryKey: ['task', taskId],
    queryFn: () => api<Task>(`/api/v1/tasks/${taskId}`),
    enabled: !!taskId,
  })
}

/**
 * The task's comment thread. `enabled` lets a caller hold the request back
 * until the Comments tab is actually shown; the cache entry is shared either
 * way (FRIDAY-74).
 */
export function useComments(taskId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['comments', taskId],
    queryFn: () => api<import('@friday/shared/realtime').Comment[]>(`/api/v1/tasks/${taskId}/comments`),
    enabled: !!taskId && enabled,
  })
}

/**
 * Task activity feed. `enabled` lets a caller hold the request back until
 * the feed is actually shown; the cache entry is shared either way.
 */
export function useActivity(taskId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['activity', 'task', taskId],
    queryFn: () => api<import('@friday/shared/realtime').Activity[]>(`/api/v1/tasks/${taskId}/activity`),
    enabled: !!taskId && enabled,
  })
}

export function useAddComment(taskId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { bodyMd: string; mentions?: string[] }) =>
      api<import('@friday/shared/realtime').Comment>(`/api/v1/tasks/${taskId}/comments`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comments', taskId] })
      void qc.invalidateQueries({ queryKey: ['activity', 'task', taskId] })
    },
  })
}

export function useMe() {
  const { user } = useAuth()
  return user
}

/** Mutation: create a project inside a workspace. Returns the new project. */
export function useCreateProject(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { key: string; name: string; description?: string; color?: string }) =>
      api<Project>(`/api/v1/workspaces/${wsId}/projects`, {
        method: 'POST',
        body: input,
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspaces', wsId, 'projects'] })
    },
  })
}

/** Mutation: archive a project (soft delete). */
export function useArchiveProject(wsId: string | undefined, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ id: string; archived: true }>(`/api/v1/projects/${pId}`, { method: 'DELETE' }),
    onSuccess: () => {
      // Sidebar + command palette share this key.
      void qc.invalidateQueries({ queryKey: ['workspaces', wsId, 'projects'] })
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      void qc.invalidateQueries({ queryKey: ['workspace-overview', wsId] })
      // The board header resolves by key, and /me/archive lists the board now.
      void qc.invalidateQueries({ queryKey: ['project-by-key', wsId] })
      void qc.invalidateQueries({ queryKey: ['archive'] })
    },
  })
}

/** Mutation: archive a task (soft delete — `archivedAt = now()`). */
export function useArchiveTask(wsId: string | undefined, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (taskId: string) =>
      api<{ id: string; deleted: false }>(`/api/v1/tasks/${taskId}`, { method: 'DELETE' }),
    onSuccess: (_data, taskId) => {
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      void qc.invalidateQueries({ queryKey: ['task', taskId] })
      void qc.invalidateQueries({ queryKey: ['workspace-overview', wsId] })
    },
  })
}

/** Mutation: hard-delete a task (real DELETE row). Use only when the task
 * is already archived — keeps a guardrail against accidental loss. */
export function useDeleteTaskHard(wsId: string | undefined, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (taskId: string) =>
      api<{ id: string; deleted: true }>(`/api/v1/tasks/${taskId}?hard=true`, { method: 'DELETE' }),
    onSuccess: (_data, taskId) => {
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      void qc.invalidateQueries({ queryKey: ['task', taskId] })
      void qc.invalidateQueries({ queryKey: ['workspace-overview', wsId] })
    },
  })
}

/** Restore a soft-archived project. PATCH with `archived: false` clears
 * `archivedAt`. The archive list and sidebar both refresh. */
export function useRestoreProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pId: string) =>
      api<{ id: string }>(`/api/v1/projects/${pId}`, {
        method: 'PATCH',
        body: { archived: false },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['archive'] })
      await qc.invalidateQueries({ queryKey: ['workspaces'] })
      await qc.invalidateQueries({ queryKey: ['workspace-overview'] })
    },
  })
}

/** Restore a soft-archived task. Same PATCH path. */
export function useRestoreTask(wsId: string | undefined, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (taskId: string) =>
      api<{ id: string }>(`/api/v1/tasks/${taskId}`, {
        method: 'PATCH',
        body: { archived: false },
      }),
    onSuccess: async (_data, taskId) => {
      await qc.invalidateQueries({ queryKey: ['archive'] })
      await qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      await qc.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })
}

/** Labels for a set of names, from the board's labels and the task's own. */
function optimisticLabels(
  names: string[],
  boardLabels: readonly { id: string; name: string; color: string }[],
  taskLabels: readonly { id: string; name: string; color: string }[],
) {
  const known = new Map([...boardLabels, ...taskLabels].map((l) => [l.name, l] as const))
  return names.map((name) => {
    const l = known.get(name)
    return l ? { id: l.id, name: l.name, color: l.color } : { id: `new:${name}`, name, color: '#9698F9' }
  })
}
